import clsx from 'clsx';

import { useWeather } from '../api/hooks';
import type { Match, MatchSet, MatchType } from '../api/types';

interface MatchCardProps {
  match: Match;
  /** "Now" reference timestamp (ISO) used to decide if a match is in progress. */
  now: string;
}

/** Hungarian match-type labels — ported verbatim from MatchCard.razor TypeText. */
function typeText(matchType: MatchType, groupName?: string | null): string {
  switch (matchType) {
    case 'Group':
      return groupName && groupName.length > 0
        ? `Csoportmérkőzés (${groupName})`
        : 'Csoportmérkőzés';
    case 'RoundOf16':
      return 'Nyolcaddöntő';
    case 'Quarterfinal':
      return 'Negyeddöntő';
    case 'SemiFinal':
      return 'Elődöntő';
    case 'Final':
      return 'Döntő';
    case 'Friendly':
      return 'Örömmecs';
    case 'PlaceDeterminer':
      return 'Helyosztó';
    default:
      return matchType;
  }
}

function formatHm(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Local midpoint between start and end, as ISO — mirrors the Blazor localMid calc. */
function midpointIso(start: string, end: string): string {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return new Date(s + (e - s) / 2).toISOString();
}

/** Best-of-3 winner; a tie defaults to team1 (mirrors CalculateTeam1Won). */
function calculateTeam1Won(sets: MatchSet[]): boolean {
  let team1Sets = 0;
  let team2Sets = 0;
  for (const s of sets) {
    if (s.team1Games > s.team2Games) team1Sets++;
    else if (s.team2Games > s.team1Games) team2Sets++;
  }
  return team1Sets >= team2Sets;
}

/**
 * MatchCard — ported from Components/Sub/MatchCard.razor.
 *
 * Two layouts: upcoming (time / court vs. teams) and result (teams vs. set
 * scores with tie-break <sup>). An in-progress match (no result yet, now within
 * [start,end]) gets the animated "live" border — a warm clay-monochrome
 * conic sweep — plus a pulsing ÉLŐ pill in the corner.
 */
export default function MatchCard({ match, now }: MatchCardProps) {
  const hasResult = match.sets.some(
    (s) => s.team1Games > 0 || s.team2Games > 0,
  );
  const showResult = hasResult;

  const nowMs = new Date(now).getTime();
  const startMs = new Date(match.start).getTime();
  const endMs = new Date(match.end).getTime();
  const inProgress = !hasResult && nowMs >= startMs && nowMs <= endMs;

  const timeRange = `${formatHm(match.start)}-${formatHm(match.end)}`;

  // Only fetch weather for not-yet-played matches (matches the Razor guard).
  const weatherAt = !showResult ? midpointIso(match.start, match.end) : undefined;
  const { data: weather } = useWeather(weatherAt);
  const weatherIcon = !showResult ? weather?.iconUrl : undefined;

  const cornerRight = showResult
    ? timeRange
    : weather
      ? `${weather.temperatureC}°C ${weather.windKmh}km/h`
      : '';

  const lostCls = (won: boolean) => (won ? '' : 'text-gray-400');
  const team1Won = showResult ? calculateTeam1Won(match.sets) : false;

  return (
    <div
      className={clsx(
        'relative bg-white rounded-lg p-4 shadow-card min-h-48',
        'transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lift',
        inProgress ? 'live-border-rg' : 'border border-line',
      )}
    >
      {/* Scoped animated "live" border — a warm clay-monochrome sweep
          (clay-deep -> clay -> bright clay -> clay-deep) that reads cleanly as
          "hot / live", plus a small pulsing ÉLŐ pill. */}
      <style>{`
        @property --mc-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes mc-grad-rotate {
          to { --mc-angle: 360deg; }
        }
        .live-border-rg::before {
          content: "";
          position: absolute;
          inset: 0;
          padding: 2px;
          border-radius: 0.5rem;
          background: conic-gradient(from var(--mc-angle), #9e3f12, #c8581e, #f0a164, #c8581e, #9e3f12);
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          animation: mc-grad-rotate 7s linear infinite;
          pointer-events: none;
        }
        @keyframes mc-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(255,255,255,.6); }
          70%  { box-shadow: 0 0 0 6px rgba(255,255,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
        .mc-live-pill {
          display: inline-flex; align-items: center; gap: 6px;
          background: #c8581e; color: #fff; font-size: 11px; font-weight: 700;
          letter-spacing: .12em; padding: 4px 10px; border-radius: 9999px;
        }
        .mc-live-dot {
          width: 7px; height: 7px; border-radius: 9999px; background: #fff;
          animation: mc-pulse 1.6s infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .live-border-rg::before { animation: none; }
          .mc-live-dot { animation: none; }
        }
      `}</style>

      <div className="flex justify-between text-sm text-gray-500 mb-10">
        <span className="opacity-70 content-center">
          {typeText(match.matchType, match.groupName)}
        </span>
        <div className="thisIsTheDirtiesHackSoFar w-8 h-8" />
        <div className="flex flex-row items-center">
          {inProgress ? (
            <span className="mc-live-pill">
              <span className="mc-live-dot" />ÉLŐ
            </span>
          ) : (
            <>
              <span className="opacity-70">{cornerRight}</span>
              {weatherIcon && (
                <img src={weatherIcon} alt="weather icon" className="w-8 h-8" />
              )}
            </>
          )}
        </div>
      </div>

      {!showResult ? (
        <div className="grid grid-cols-2 gap-x-4 items-start">
          <div className="text-lg font-semibold text-center space-y-2">
            <div>{timeRange}</div>
            <div>{`${match.courtNumber}. Pálya`}</div>
          </div>
          <div className="flex flex-col items-center w-full text-base font-medium">
            <div className="mb-1 text-center">{match.team1}</div>
            <hr className="w-full border-t border-line my-1" />
            <div className="mt-1 text-center">{match.team2}</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 items-start text-lg font-semibold">
          <div className="flex flex-col gap-3">
            <span className={lostCls(team1Won)}>{match.team1}</span>
            <span className={lostCls(!team1Won)}>{match.team2}</span>
          </div>
          <div className="flex flex-col gap-3 items-end">
            <div className="flex gap-4">
              {match.sets.map((set, i) => (
                <span
                  key={i}
                  className={
                    set.team1Games > set.team2Games
                      ? ''
                      : 'font-semibold text-gray-400'
                  }
                >
                  {set.team1Games}
                  {set.team1TieBreakPoints != null && (
                    <sup>{set.team1TieBreakPoints}</sup>
                  )}
                </span>
              ))}
            </div>
            <div className="flex gap-4">
              {match.sets.map((set, i) => (
                <span
                  key={i}
                  className={
                    set.team2Games > set.team1Games
                      ? ''
                      : 'font-semibold text-gray-400'
                  }
                >
                  {set.team2Games}
                  {set.team2TieBreakPoints != null && (
                    <sup>{set.team2TieBreakPoints}</sup>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
